import React from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useWebSocket } from '../hooks/useWebSocket';
import WebSocketIndicator from './WebSocketIndicator';
import {
  LayoutDashboard,
  AlertTriangle,
  Activity,
  Database,
  Users,
  FileText,
  LogOut,
  Menu,
  X,
  UserCog,
  BarChart3,
  Shield,
  Calendar,
  ShieldOff,
  Clock,
  FileStack,
  ClipboardList,
  LineChart,
  Briefcase,
  Network,
  MapPin,
  Palette
} from 'lucide-react';
import { Button } from './ui/button';

const Sidebar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const [isOpen, setIsOpen] = React.useState(false);
  const [branding, setBranding] = React.useState(null);

  React.useEffect(() => {
    const loadBranding = async () => {
      try {
        const { default: api } = await import('../utils/axios');
        const res = await api.get('/branding');
        if (res.data) setBranding(res.data);
      } catch {}
    };
    loadBranding();
  }, []);

  const menuItems = [
    { icon: LayoutDashboard, label: 'Dashboard', path: '/dashboard', testId: 'nav-dashboard' },
    { icon: LineChart, label: 'Ejecutivo', path: '/executive', testId: 'nav-executive' },
    { icon: AlertTriangle, label: 'Alertas', path: '/alerts', testId: 'nav-alerts' },
    { icon: Briefcase, label: 'Casos', path: '/cases', testId: 'nav-cases' },
    { icon: Calendar, label: 'Calendario', path: '/calendar', testId: 'nav-calendar' },
    { icon: Activity, label: 'Monitores', path: '/monitors', testId: 'nav-monitors' },
    { icon: FileStack, label: 'Plantillas', path: '/monitor-templates', testId: 'nav-templates' },
    { icon: Database, label: 'Movimientos', path: '/movimientos', testId: 'nav-movimientos' },
    { icon: Users, label: 'Padrón', path: '/padron', testId: 'nav-padron' },
    { icon: Network, label: 'Vínculos', path: '/links', testId: 'nav-links' },
    { icon: MapPin, label: 'Mapa Calor', path: '/geo-heatmap', testId: 'nav-geo' },
    { icon: Shield, label: 'Risk Scoring', path: '/risk-scoring', testId: 'nav-risk-scoring' },
    { icon: ClipboardList, label: 'Matriz MRC', path: '/mrc-config', testId: 'nav-mrc' },
    { icon: ShieldOff, label: 'Black/Whitelists', path: '/account-lists', testId: 'nav-lists' },
    { icon: Clock, label: 'Escalamiento', path: '/escalation', testId: 'nav-escalation' },
    { icon: BarChart3, label: 'Reportes', path: '/reports', testId: 'nav-reports' },
    { icon: FileText, label: 'Auditoría', path: '/audit', testId: 'nav-audit' },
    { icon: UserCog, label: 'Usuarios', path: '/users', testId: 'nav-users' },
    { icon: Palette, label: 'Branding', path: '/branding', testId: 'nav-branding' },
  ];

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // Inline styles to override any Emergent preview styles
  const sidebarBg = { backgroundColor: '#0f172a' };
  const textWhite = { color: '#ffffff' };
  const textSlate300 = { color: '#cbd5e1' };
  const textSlate400 = { color: '#94a3b8' };
  const borderSlate800 = { borderColor: '#1e293b' };
  const activeBtnBg = { backgroundColor: '#2563eb', color: '#ffffff' };
  const hoverBtnBg = { backgroundColor: '#1e293b' };
  const logoutBtnStyle = { backgroundColor: '#1e293b', borderColor: '#334155', color: '#ffffff' };

  return (
    <>
      {/* Mobile menu button */}
      <button
        data-testid="mobile-menu-toggle"
        className="lg:hidden fixed top-4 left-4 z-50 p-2 rounded-md"
        style={{ backgroundColor: '#0f172a', color: '#ffffff' }}
        onClick={() => setIsOpen(!isOpen)}
      >
        {isOpen ? <X size={24} /> : <Menu size={24} />}
      </button>

      {/* Sidebar */}
      <div
        className={`
          fixed lg:static inset-y-0 left-0 z-40
          w-64
          transform transition-transform duration-200 ease-in-out
          ${isOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
        `}
        style={sidebarBg}
      >
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className="p-6 border-b" style={{ ...borderSlate800, ...sidebarBg }}>
            <div className="flex items-center gap-3">
              {branding?.logo_header ? (
                <img src={branding.logo_header} alt="Logo" className="h-8 object-contain" />
              ) : null}
              <div>
                <h1 className="text-xl font-heading font-bold tracking-tight" style={textWhite}>
                  {branding?.nombre_empresa || 'AlertGuard'}
                </h1>
                <p className="text-xs mt-1" style={textSlate400}>Monitoreo Transaccional</p>
              </div>
            </div>
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-4 space-y-1" style={sidebarBg}>
            {menuItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;
              return (
                <button
                  key={item.path}
                  data-testid={item.testId}
                  onClick={() => {
                    navigate(item.path);
                    setIsOpen(false);
                  }}
                  className="w-full flex items-center space-x-3 px-4 py-2.5 rounded-md transition-all duration-200"
                  style={isActive ? activeBtnBg : textSlate300}
                  onMouseEnter={(e) => {
                    if (!isActive) {
                      e.currentTarget.style.backgroundColor = '#1e293b';
                      e.currentTarget.style.color = '#ffffff';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!isActive) {
                      e.currentTarget.style.backgroundColor = 'transparent';
                      e.currentTarget.style.color = '#cbd5e1';
                    }
                  }}
                >
                  <Icon size={20} style={{ color: 'inherit' }} />
                  <span className="text-sm font-medium">{item.label}</span>
                </button>
              );
            })}
          </nav>

          {/* User info & logout */}
          <div className="p-4 border-t" style={{ ...borderSlate800, ...sidebarBg }}>
            <div className="flex items-center space-x-3 mb-3">
              <div 
                className="w-10 h-10 rounded-full flex items-center justify-center"
                style={{ backgroundColor: '#2563eb' }}
              >
                <span className="text-sm font-bold" style={textWhite}>
                  {user?.nombre?.charAt(0).toUpperCase()}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate" style={textWhite}>{user?.nombre}</p>
                <p className="text-xs truncate" style={textSlate400}>{user?.email}</p>
              </div>
            </div>
            <Button
              data-testid="logout-button"
              onClick={handleLogout}
              variant="outline"
              size="sm"
              className="w-full"
              style={logoutBtnStyle}
            >
              <LogOut size={16} className="mr-2" style={textWhite} />
              <span style={textWhite}>Cerrar Sesión</span>
            </Button>
          </div>
        </div>
      </div>

      {/* Overlay for mobile */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-30 lg:hidden"
          onClick={() => setIsOpen(false)}
        />
      )}
    </>
  );
};

export const Layout = () => {
  const { user } = useAuth();
  const { isConnected } = useWebSocket(user?.id);
  
  return (
    <div className="flex h-screen overflow-hidden bg-slate-50 dark:bg-slate-900">
      <Sidebar />
      <main className="flex-1 overflow-auto bg-slate-50 dark:bg-slate-900 relative">
        {/* WebSocket status indicator */}
        <div className="absolute top-4 right-4 z-10">
          <WebSocketIndicator isConnected={isConnected} />
        </div>
        <Outlet />
      </main>
    </div>
  );
};

export default Layout;
